import Color from 'color';

import { INITIAL_USER_DELETE_COLOR, INITIAL_USER_EDIT_COLOR } from './theme-colors';
import { AI_DELETE_HIGHLIGHT_COLOR, AI_INSERT_HIGHLIGHT_COLOR, primaryColor } from '../rendering';
import { DELETE_POLARITY_TYPES, INSERT_POLARITY_TYPES, isTextFormatEdit } from '../slate-api';
import type { SuggestedEdit } from '../slate-api';

/**
 * Re-exported from the side-effect-free `theme-colors` leaf (this module runs
 * module-scoped side effects on import, so consumers that only need these
 * colors — e.g. the MUI theme — import them from the leaf instead).
 */
export { INITIAL_USER_DELETE_COLOR, INITIAL_USER_EDIT_COLOR };

/** Re-exported from the palette in `rendering/colors`, where they are defined. */
export { AI_DELETE_HIGHLIGHT_COLOR, AI_INSERT_HIGHLIGHT_COLOR };

const PRESENCE_COLORS_KEY = 'presence-colors-assignemnt-order';

/**
 * Use the hue of primaryColor as the starting hue. Note that primaryColor.lch().hue() does not
 * produce the same result, since hues in LCh are different to those in HSL, which is what hue()
 * returns.
 */
const HUE_INCREMENTS = 3;
const HUE_INCREMENT = 360 / HUE_INCREMENTS;
export const START_HUE = primaryColor.lch().array()[2];

const hues = getHues(START_HUE);

// Skip the first generated hue because it is too close to the primary color.
hues.next();

// The next hue is the current user, matching MS Word.
const currentUserHue = hues.next().value ?? -1;
export const CURRENT_USER_PRESENCE_COLOR = colorForHue(currentUserHue);

/** The presence color assigned to each user ID. */
const assignedPresenceColors = new Map<string, string>();
initializePresenceColors();

/** Determine the color used to display a suggested text edit. */
export function getEditColor(
  edit: SuggestedEdit,
  options: { currentUserId: string | null }
): string {
  if (options.currentUserId === edit.authorId) {
    if (INSERT_POLARITY_TYPES.has(edit.type) || isTextFormatEdit(edit))
      return INITIAL_USER_EDIT_COLOR;
    if (DELETE_POLARITY_TYPES.has(edit.type)) return INITIAL_USER_DELETE_COLOR;
  }

  return getPresenceColor(edit.authorId, options);
}

/**
 * Get the presence color for a given user ID, or assign one sequentially if one has not already
 * been assigned.
 */
export function getPresenceColor(
  userId: string,
  options: { currentUserId: string | null }
): string {
  const { currentUserId } = options;
  if (userId === currentUserId) return CURRENT_USER_PRESENCE_COLOR;

  let assignedColor = assignedPresenceColors.get(userId);

  if (!assignedColor) {
    assignedColor = colorForHue(hues.next().value ?? -1);
    assignedPresenceColors.set(userId, assignedColor);
    updateStoredAssignmentOrder();
  }

  return assignedColor;
}

/**
 * Generator function that produces an infinite sequence of hues. It does this by making repeated
 * cycles around the color wheel. At the end of each cycle, it applies a unique phase offset and
 * continues with the same increment as before. This ensures that each hue is only used once.
 *
 * @private Exported for testing purposes only.
 */
export function* getHues(startHue: number): Generator<number, void, undefined> {
  const phaseOffsets = getPhaseOffsets();

  let hue = startHue;
  let index = 0;

  while (true) {
    yield hue % 360;

    hue += HUE_INCREMENT;
    index++;

    if (index % HUE_INCREMENTS === 0) {
      const phaseOffset = phaseOffsets.next().value;
      if (phaseOffset === undefined) throw new Error('Expected phase offset');
      hue = startHue + phaseOffset;
    }
  }
}

/**
 * Generator function that produces an infinite sequence of unique fractions of the hue increment,
 * starting with 1/2. It then subdivides the gaps between each pair of already used fractions (in
 * addition to and 0 and 1), resulting in 1/4, 3/4, 1/8, 3/8, 5/8, 7/8, etc.
 */
function* getPhaseOffsets(): Generator<number, void, undefined> {
  let phaseOffsetNumerator = 1;
  let phaseOffsetDenominator = 2;

  while (true) {
    yield (HUE_INCREMENT * phaseOffsetNumerator) / phaseOffsetDenominator;
    phaseOffsetNumerator += 2;
    if (phaseOffsetNumerator >= phaseOffsetDenominator) {
      phaseOffsetNumerator = 1;
      phaseOffsetDenominator *= 2;
    }
  }
}

/**
 * Produce a hex color for a given LCh hue. Oklch might produce better results, but is not currently
 * supported by the color package.
 *
 * Lightness and chroma values in LCh (and Oklch) have maximum values that vary depending on the hue
 * and on each other. Attempting to display an unsupported hue/lightness/chroma combination may
 * result in a fallback color that looks completely different to the intended color. For this
 * reason, lightness/chroma pairs must be chosen carefully to produce consistent-looking colors for
 * all hues.
 *
 * When changing this function, rerun the tests and check ./__tests__/presence-colors.svg to ensure
 * that all 'normal' variant colors are suitable. You will also need to manually check that the
 * 'pale' color used to highlight AI edits has sufficient contrast to the corresponding 'normal'
 * color.
 *
 * @private Exported for testing purposes only.
 */
export function colorForHue(hue: number, variant: 'normal' | 'pale' = 'normal'): string {
  if (hue === -1) throw new Error('Invalid hue');
  return Color.lch(variant === 'normal' ? 60 : 95, variant === 'normal' ? 130 : 20, hue).hex();
}

/**
 * Initialize the map of assigned presence colors from session storage for consistency across
 * refreshes. We use use session storage rather than local storage mainly to aovid storing user IDs
 * in browser storage for extended periods of time (potentially a privacy
 * issue on shared devices).
 */
function initializePresenceColors() {
  if (typeof window === 'undefined') return [];

  const data = window.sessionStorage.getItem(PRESENCE_COLORS_KEY);
  if (!data) return [];

  let userIdsInAssignmentOrder: string[];

  try {
    userIdsInAssignmentOrder = JSON.parse(data);
  } catch (error) {
    console.error(error);
    return [];
  }

  if (!Array.isArray(userIdsInAssignmentOrder)) {
    console.error(`Unexpected format for ${PRESENCE_COLORS_KEY}`);
    return [];
  }

  // Reassign the same colors to users in the same order as before
  for (const userId of userIdsInAssignmentOrder) {
    getPresenceColor(userId, { currentUserId: null });
  }
}

function updateStoredAssignmentOrder() {
  if (typeof window === 'undefined') return;
  // Map.prototype.keys() is guaranteed by the spec to return keys in insertion order
  const userIdsInAssignmentOrder = assignedPresenceColors.keys().toArray();
  const data = JSON.stringify(userIdsInAssignmentOrder);
  window.sessionStorage.setItem(PRESENCE_COLORS_KEY, data);
}
